import React from 'react';
import { UserRound, Phone, Mail, CalendarDays, Clock, FileSignature, AlertTriangle, LayoutGrid, Loader2 } from 'lucide-react';

const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtWhen = (ts) => (ts ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never');
const agreementDate = (d) => (d?.year ? `${d.month}/${d.day}/${d.year}` : '');

const Line = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-3.5 h-3.5 text-[#9599C0] mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-[#9599C0]">{label}</p>
      <p className="text-[12px] text-[#0A1551] break-words" data-testid={testid}>{value || '—'}</p>
    </div>
  </div>
);

export const CustomerPanel = ({ customer: c, failed }) => {
  if (failed) return <div className="bg-white border border-[#E3E5F5] rounded-xl p-4 text-[12px] text-[#6F76A7]" data-testid="customer-panel-error">Could not load this customer&apos;s details.</div>;
  if (!c) return <div className="bg-white border border-[#E3E5F5] rounded-xl p-4 flex items-center gap-2 text-[12px] text-[#6F76A7]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading customer…</div>;

  const signed = c.agreements?.[0];

  return (
    <div className="bg-white border border-[#E3E5F5] rounded-xl p-4 space-y-4" data-testid="customer-panel">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-full bg-[#E6F4F2] flex items-center justify-center text-[13px] font-bold text-[#0F766E]">
          {(c.name || c.email || '?').charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-[#0A1551] truncate" data-testid="customer-name">{c.name || 'Unnamed'}</p>
          <p className="text-[11px] text-[#6F76A7]">{c.session_active ? 'Online now' : `Last seen ${fmtWhen(c.last_seen)}`}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 pt-1 border-t border-[#F0F1FB]">
        <Line icon={Mail} label="Email" value={c.email} testid="customer-email" />
        <Line icon={Phone} label="Phone" value={c.phone} testid="customer-phone" />
        <Line icon={CalendarDays} label="Signed up" value={fmtDate(c.signed_up)} testid="customer-signed-up" />
        <Line icon={Clock} label="Last seen" value={fmtWhen(c.last_seen)} testid="customer-last-seen" />
      </div>

      <div className="pt-3 border-t border-[#F0F1FB]">
        <p className="text-[10px] uppercase tracking-wide text-[#9599C0] mb-1.5">Investor agreement</p>
        {signed ? (
          <div className="flex items-start gap-2" data-testid="customer-agreement-signed">
            <FileSignature className="w-3.5 h-3.5 text-[#0AA24F] mt-0.5 shrink-0" />
            <p className="text-[12px] text-[#0A1551]">
              Signed {agreementDate(signed.date) || fmtDate(signed.submitted_at)}
              {c.agreements.length > 1 && <span className="text-[#6F76A7]"> · {c.agreements.length} on file</span>}
            </p>
          </div>
        ) : (
          <p className="text-[12px] text-[#6F76A7]" data-testid="customer-agreement-none">Not signed yet</p>
        )}
        {c.unfinished && (
          <div className="mt-2 flex items-start gap-2 bg-[#FFF7E0] rounded-md px-2 py-1.5" data-testid="customer-unfinished">
            <AlertTriangle className="w-3.5 h-3.5 text-[#B45309] mt-0.5 shrink-0" />
            <p className="text-[11px] text-[#7C5B14]">
              Started but never signed on {fmtDate(c.unfinished.created_at)}
              {c.unfinished.reminders_sent > 0 && ` · ${c.unfinished.reminders_sent} reminder${c.unfinished.reminders_sent > 1 ? 's' : ''} sent`}
            </p>
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-[#F0F1FB]">
        <p className="text-[10px] uppercase tracking-wide text-[#9599C0] mb-1.5">In the workspace</p>
        <div className="flex items-center gap-2 text-[12px] text-[#0A1551]" data-testid="customer-workspace-counts">
          <LayoutGrid className="w-3.5 h-3.5 text-[#9599C0]" />
          {c.forms} form{c.forms === 1 ? '' : 's'} · {c.submissions} submission{c.submissions === 1 ? '' : 's'} · {c.teams} team{c.teams === 1 ? '' : 's'}
        </div>
      </div>
    </div>
  );
};

export default CustomerPanel;
